import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import Modal from "./Modal";
import "./NewNoteModal.css";
import toast from "react-hot-toast";
import * as signalR from "@microsoft/signalr";
import "emoji-picker-element";

const NewNoteModal = forwardRef(
  (
    {
      isOpen,
      onClose,
      refreshNotes,
      refreshFilteredNotes,
      addSiteNote,
      prefilledData = null,
      defWorkSpaceId,
      userworksaces = [],
      source = "dashboard",
      defaultWorkspaceRole,
      fetchNotesWithFilters,
      selectedFilters,
      hasActiveFilters,
      viewMode = "cards",
      refreshStackedView = null,
      hasActiveSearchText,
      performSearching,
      searchTerm,
      fetchNotesForTask,
      fetchTasksForJob

    },
    ref,
  ) => {
    // ---------- State ----------
    const [activeTab, setActiveTab] = useState("journal");
    const [selectedProject, setSelectedProject] = useState("");
    const [selectedPriority, setSelectedPriority] = useState("1");
    const [selectedJob, setSelectedJob] = useState("");
    const [selectedWorkspace, setSelectedWorkspace] = useState("");
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [noteContent, setNoteContent] = useState("");
    const [documents, setDocuments] = useState([]);
    const [newDocument, setNewDocument] = useState({ name: "", file: null });
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [currentDocumentBeingEdited, setCurrentDocumentBeingEdited] = useState(null);
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [fetchedProjects, setFetchedProjects] = useState([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [isLoadingJobs, setIsLoadingJobs] = useState(false);
    const connectionRef = useRef(null);
    const [isSignalRConnected, setIsSignalRConnected] = useState(false);
    const modalRef = useRef(null);

    // Search states
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [allSearchData, setAllSearchData] = useState([]);

    // Store selected objects
    const [selectedProjectData, setSelectedProjectData] = useState(null);
    const [selectedJobData, setSelectedJobData] = useState(null);

    // Rich text editor states
    const [richTextContent, setRichTextContent] = useState("");
    const [pastedImages, setPastedImages] = useState([]);

    // Emoji picker states
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef(null);
    const emojiButtonRef = useRef(null);

    const editorRef = useRef(null);
    const hasFocusedRef = useRef(false);
    const searchInputRef = useRef(null);
    const colorPickerRef = useRef(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [selectedColor, setSelectedColor] = useState("#000000");

    //const [taskName, setTaskName] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [hasUserDragged, setHasUserDragged] = useState(false);

    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;
    const isReadOnly = defaultWorkspaceRole === 3;
    const [taskName, setTaskName] = useState(prefilledData?.taskName);
    const [taskId, setTaskId] = useState(prefilledData?.taskid);


    // Constants (moved outside component or useMemo)
    const ALLOWED_FILE_TYPES = useMemo(
      () => ({
        "image/jpeg": [".jpg", ".jpeg"],
        "image/png": [".png"],
        "image/gif": [".gif"],
        "image/webp": [".webp"],
        "image/svg+xml": [".svg"],
        "application/pdf": [".pdf"],
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
        "application/vnd.ms-excel": [".xls"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        "application/vnd.ms-powerpoint": [".ppt"],
        "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
        "text/plain": [".txt"],
        "audio/mpeg": [".mp3"],
        "audio/wav": [".wav"],
        "audio/ogg": [".ogg"],
        "audio/aac": [".aac"],
        "video/mp4": [".mp4"],
        "video/mpeg": [".mpeg"],
        "video/ogg": [".ogv"],
        "video/webm": [".webm"],
        "video/quicktime": [".mov"],
        "video/x-msvideo": [".avi"],
      }),
      [],
    );

    const colorOptions = useMemo(
      () => [
        { name: "Black", value: "#000000" },
        { name: "Dark Gray", value: "#666666" },
        { name: "Gray", value: "#999999" },
        { name: "Light Gray", value: "#cccccc" },
        { name: "White", value: "#ffffff" },
        { name: "Red", value: "#ff0000" },
        { name: "Dark Red", value: "#8b0000" },
        { name: "Orange", value: "#ff9900" },
        { name: "Brown", value: "#8b4513" },
        { name: "Yellow", value: "#ffff00" },
        { name: "Green", value: "#008000" },
        { name: "Light Green", value: "#90ee90" },
        { name: "Blue", value: "#0000ff" },
        { name: "Light Blue", value: "#add8e6" },
        { name: "Dark Blue", value: "#00008b" },
        { name: "Purple", value: "#800080" },
        { name: "Pink", value: "#ffc0cb" },
        { name: "Hot Pink", value: "#ff69b4" },
        { name: "Cyan", value: "#00ffff" },
        { name: "Teal", value: "#008080" },
      ],
      [],
    );

    // Helper functions (memoized)
    const getCurrentUser = useCallback(() => {
      const user = JSON.parse(localStorage.getItem("user"));
      return user || { id: 1 };
    }, []);

    const formatDate = useCallback((dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }, []);

    const safelyRenderHTML = useCallback((html) => {
      if (!html) return { __html: "" };
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const dangerous = doc.querySelectorAll(
        "script, style, iframe, object, embed, form, input, button, textarea, select",
      );
      dangerous.forEach((el) => el.remove());
      const allElements = doc.body.querySelectorAll("*");
      allElements.forEach((el) => {
        Array.from(el.attributes).forEach((attr) => {
          if (attr.name.startsWith("on")) el.removeAttribute(attr.name);
        });
        ["href", "src", "action"].forEach((attr) => {
          const value = el.getAttribute(attr);
          if (value && value.toLowerCase().startsWith("javascript:"))
            el.removeAttribute(attr);
        });
      });
      return { __html: doc.body.innerHTML };
    }, []);

    // Memoized selectedWorkspaceData
    const selectedWorkspaceData = useMemo(() => {
      if (!selectedWorkspace || !userworksaces.length) return null;
      return userworksaces.find((w) => w.id.toString() === selectedWorkspace);
    }, [selectedWorkspace, userworksaces]);

    // ----- Normalization helpers (memoized) -----
    const normalizeProjectData = useCallback((projectData, fallbackData = null) => {
      const data = projectData || fallbackData;
      if (!data) return null;
      return {
        id: data.id || data.projectId,
        projectId: data.projectId || data.id,
        name: data.name || data.projectName || "Unnamed Project",
        workspaceId: data.workspaceId || (fallbackData ? fallbackData.workspaceId : null),
        ...data,
      };
    }, []);

    const normalizeJobData = useCallback((jobData, fallbackData = null) => {
      const data = jobData || fallbackData;
      if (!data) return null;
      return {
        id: data.id || data.jobId,
        jobId: data.jobId || data.id,
        name: data.name || data.jobName || "Unnamed Job",
        projectId: data.projectId || (fallbackData ? fallbackData.projectId : null),
        ...data,
      };
    }, []);

    // ----- Memoized project/job options -----
    const projectOptions = useMemo(() => {
      const options = [...filteredProjects];
      if (
        selectedProjectData &&
        (selectedProjectData.id || selectedProjectData.projectId)
      ) {
        const projectId = (
          selectedProjectData.id || selectedProjectData.projectId
        ).toString();
        const exists = options.some((p) => {
          const pId = (p.id || p.projectId)?.toString();
          return pId === projectId;
        });
        if (!exists) options.push(selectedProjectData);
      }
      return options.filter((p) => p && (p.id || p.projectId));
    }, [filteredProjects, selectedProjectData]);

    const jobOptions = useMemo(() => {
      const options = [...filteredJobs];
      if (selectedJobData && (selectedJobData.id || selectedJobData.jobId)) {
        const jobId = (selectedJobData.id || selectedJobData.jobId).toString();
        const exists = options.some((j) => {
          const jId = (j.id || j.jobId)?.toString();
          return jId === jobId;
        });
        if (!exists) options.push(selectedJobData);
      }
      return options.filter((j) => j && (j.id || j.jobId));
    }, [filteredJobs, selectedJobData]);

    // ----- Rich text utilities (memoized) -----
    const processPastedImage = useCallback((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Image = e.target.result;
          const imageId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const imageName = file.name || `image-${Date.now()}.png`;
          const img = document.createElement("img");
          img.src = base64Image;
          img.alt = "Pasted image";
          img.className = "editor-image";
          img.dataset.imageId = imageId;
          img.dataset.imageName = imageName;
          img.dataset.imageBase64 = base64Image;
          setPastedImages((prev) => [
            ...prev,
            {
              id: imageId,
              name: imageName,
              base64: base64Image,
              type: file.type,
              size: file.size,
            },
          ]);
          resolve(img);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }, []);

    const cleanHtmlContent = useCallback((html) => {
      let cleaned = html.replace(/<o:p>|<\/o:p>/gi, "");
      cleaned = cleaned.replace(/<w:.*?>|<\/w:.*?>/gi, "");
      cleaned = cleaned.replace(/<xml.*?>.*?<\/xml>/gi, "");
      cleaned = cleaned.replace(/ class="[^"]*"/gi, "");
      cleaned = cleaned.replace(/ id="[^"]*"/gi, "");
      cleaned = cleaned.replace(/<b(\s[^>]*)?>/gi, "<strong>").replace(/<\/b>/gi, "</strong>");
      cleaned = cleaned.replace(/<i(\s[^>]*)?>/gi, "<em>").replace(/<\/i>/gi, "</em>");
      cleaned = cleaned.replace(/<p>\s*<\/p>/gi, "");
      cleaned = cleaned.replace(/<div>\s*<\/div>/gi, "");
      cleaned = cleaned.replace(/<span>\s*<\/span>/gi, "");
      return cleaned;
    }, []);

    const convertWordListsToHtml = useCallback((container) => {
      // Implementation unchanged (too long but memoized)
      const paragraphs = container.querySelectorAll("p");
      const listItems = [];
      paragraphs.forEach((p) => {
        const style = p.getAttribute("style") || "";
        if (
          style.includes("mso-list") ||
          (style.includes("text-indent") && style.includes("-")) ||
          p.innerHTML.match(/^[••●○■□▪︎►✓☐☑]/) ||
          p.innerHTML.match(/^\d+[\.\)]/)
        ) {
          let level = 0;
          const levelMatch = style.match(/mso-list:[^;]*level(\d+)/);
          if (levelMatch) level = parseInt(levelMatch[1]) || 0;
          listItems.push({ element: p, level, html: p.innerHTML });
        }
      });
      if (listItems.length === 0) return;
      let currentList = null;
      let currentListType = null;
      listItems.forEach((item, index) => {
        const isNumbered = item.html.match(/^\d+[\.\)]/);
        const listType = isNumbered ? "ol" : "ul";
        if (!currentList || currentListType !== listType) {
          currentList = document.createElement(listType);
          currentList.style.marginLeft = "20px";
          currentList.style.paddingLeft = "20px";
          item.element.parentNode.insertBefore(currentList, item.element);
          currentListType = listType;
        }
        const li = document.createElement("li");
        let content = item.html;
        content = content.replace(/^(\d+[\.\)]\s*)/, "");
        content = content.replace(/^[••●○■□▪︎►]\s*/, "");
        content = content.replace(/^<\/?[^>]+>\s*/, "");
        li.innerHTML = content;
        if (item.level > 0 && currentList.lastChild && currentList.lastChild.tagName === "LI") {
          const parentLi = currentList.lastChild;
          let nestedList = parentLi.querySelector(listType);
          if (!nestedList) {
            nestedList = document.createElement(listType);
            nestedList.style.marginLeft = "20px";
            nestedList.style.paddingLeft = "20px";
            parentLi.appendChild(nestedList);
          }
          nestedList.appendChild(li);
        } else {
          currentList.appendChild(li);
        }
        item.element.remove();
      });
    }, []);

    const processNegativeIndentElements = useCallback((container) => {
      const elementsWithIndent = container.querySelectorAll('[style*="text-indent"]');
      elementsWithIndent.forEach((el) => {
        const style = el.getAttribute("style") || "";
        if (style.includes("text-indent") && style.includes("-")) {
          if (el.tagName === "P" || el.tagName === "DIV" || el.tagName === "SPAN") {
            const content = el.textContent || "";
            const isBullet = content.match(/^[••●○■□▪︎►]/);
            if (isBullet) {
              const parent = el.parentNode;
              let list = parent.querySelector("ul");
              if (!list) {
                list = document.createElement("ul");
                list.style.marginLeft = "30px";
                list.style.paddingLeft = "20px";
                el.parentNode.insertBefore(list, el);
              }
              const li = document.createElement("li");
              li.innerHTML = el.innerHTML.replace(/^[••●○■□▪︎►]\s*/, "");
              list.appendChild(li);
              el.remove();
            } else {
              el.style.textIndent = "0";
              el.style.marginLeft = "20px";
              el.style.paddingLeft = "20px";
            }
          } else {
            el.style.textIndent = "0";
            el.style.marginLeft = "20px";
            el.style.paddingLeft = "20px";
          }
        }
      });
      const allElements = container.querySelectorAll("p, div, span");
      allElements.forEach((el) => {
        const content = el.textContent || "";
        const firstChar = content.trim().charAt(0);
        if (firstChar.match(/[••●○■□▪︎►]/) && !el.closest("ul, ol")) {
          const parent = el.parentNode;
          let list = parent.querySelector("ul");
          if (!list) {
            list = document.createElement("ul");
            list.style.marginLeft = "30px";
            list.style.paddingLeft = "20px";
            el.parentNode.insertBefore(list, el);
          }
          const li = document.createElement("li");
          li.innerHTML = el.innerHTML.replace(/^[••●○■□▪︎►]\s*/, "");
          list.appendChild(li);
          el.remove();
        }
      });
    }, []);

    const insertHtmlSafely = useCallback((htmlContent) => {
      try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlContent;
        const cleanBulletPoints = (element) => {
          const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            {
              acceptNode: (node) =>
                node.textContent && node.textContent.match(/[••●○■□▪︎►]/)
                  ? NodeFilter.FILTER_ACCEPT
                  : NodeFilter.FILTER_SKIP,
            },
          );
          const textNodesToClean = [];
          let node;
          while ((node = walker.nextNode())) textNodesToClean.push(node);
          textNodesToClean.forEach((textNode) => {
            let cleanText = textNode.textContent.replace(/^[••●○■□▪︎►]\s*/, "");
            cleanText = cleanText.replace(/[••●○■□▪︎►]/g, "");
            textNode.textContent = cleanText;
          });
          const elements = element.querySelectorAll("*");
          elements.forEach((el) => {
            if (el.innerHTML) {
              let cleanHtml = el.innerHTML;
              cleanHtml = cleanHtml.replace(/^[••●○■□▪︎►]\s*/, "");
              cleanHtml = cleanHtml.replace(/[••●○■□▪︎►]/g, "");
              el.innerHTML = cleanHtml;
            }
          });
        };
        cleanBulletPoints(tempDiv);
        document.execCommand("insertHTML", false, tempDiv.innerHTML);
        if (editorRef.current) setRichTextContent(editorRef.current.innerHTML);
      } catch (error) {
        console.error("Failed to insert HTML:", error);
        const plainText = htmlContent.replace(/<[^>]*>/g, "");
        document.execCommand("insertText", false, plainText);
        if (editorRef.current) setRichTextContent(editorRef.current.innerHTML);
      }
    }, []);

    const handlePaste = useCallback(
      (e) => {
        if (!editorRef.current || isReadOnly) return;
        e.preventDefault();
        const clipboardData = e.clipboardData || window.clipboardData;
        if (clipboardData.files && clipboardData.files.length > 0) {
          const file = clipboardData.files[0];
          if (file.type.startsWith("image/")) {
            processPastedImage(file)
              .then((imgElement) => {
                const selection = window.getSelection();
                if (selection.rangeCount > 0) {
                  const range = selection.getRangeAt(0);
                  range.deleteContents();
                  range.insertNode(imgElement);
                  const space = document.createTextNode(" ");
                  range.insertNode(space);
                  range.setStartAfter(space);
                  range.collapse(true);
                }
                setRichTextContent(editorRef.current.innerHTML);
              })
              .catch((error) => {
                console.error("Error processing pasted image:", error);
                const plainText = clipboardData.getData("text/plain");
                if (plainText) document.execCommand("insertText", false, plainText);
              });
            return;
          }
        }
        let htmlContent = clipboardData.getData("text/html");
        if (htmlContent) {
          htmlContent = cleanHtmlContent(htmlContent);
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = htmlContent;
          // Reduce font sizes
          const allElements = tempDiv.querySelectorAll("*");
          allElements.forEach((el) => {
            if (el.style && el.style.fontSize) {
              const fontSize = el.style.fontSize;
              const match = fontSize.match(/^([\d.]+)(px|pt|em|rem|%)?$/);
              if (match) {
                const value = parseFloat(match[1]);
                const unit = match[2] || "px";
                const reducedValue = value * 0.45;
                el.style.fontSize = reducedValue + unit;
              }
            }
          });
          convertWordListsToHtml(tempDiv);
          processNegativeIndentElements(tempDiv);
          const lists = tempDiv.querySelectorAll("ul, ol");
          lists.forEach((list) => {
            list.style.marginLeft = "20px";
            list.style.paddingLeft = "20px";
            list.style.listStylePosition = "outside";
            if (list.tagName === "UL") list.style.listStyleType = "disc";
            else list.style.listStyleType = "decimal";
            const listItems = list.querySelectorAll("li");
            listItems.forEach((li) => {
              li.style.marginLeft = "0";
              li.style.paddingLeft = "0";
              li.style.textIndent = "0";
              li.style.display = "list-item";
            });
          });
          insertHtmlSafely(tempDiv.innerHTML);
          return;
        }
        const plainText = clipboardData.getData("text/plain");
        if (plainText) {
          document.execCommand("insertText", false, plainText);
          setRichTextContent(editorRef.current.innerHTML);
        }
      },
      [isReadOnly, processPastedImage, cleanHtmlContent, convertWordListsToHtml, processNegativeIndentElements, insertHtmlSafely],
    );

    const handleEditorInput = useCallback(() => {
      if (editorRef.current) setRichTextContent(editorRef.current.innerHTML);
    }, []);

    const clearEditor = useCallback(() => {
      if (isReadOnly) return;
      if (editorRef.current) {
        editorRef.current.innerHTML = "";
        setRichTextContent("");
      }
      setPastedImages([]);
    }, [isReadOnly]);

    const formatText = useCallback(
      (command, value = null) => {
        if (isReadOnly) return;
        document.execCommand(command, false, value);
        if (editorRef.current) setRichTextContent(editorRef.current.innerHTML);
        if (editorRef.current) editorRef.current.focus();
      },
      [isReadOnly],
    );

    const handleImageUpload = useCallback(
      (e) => {
        if (isReadOnly) return;
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
          toast.error("Please select an image file");
          return;
        }
        processPastedImage(file)
          .then((imgElement) => {
            if (editorRef.current) {
              editorRef.current.appendChild(imgElement);
              editorRef.current.appendChild(document.createTextNode(" "));
              setRichTextContent(editorRef.current.innerHTML);
              editorRef.current.scrollTop = editorRef.current.scrollHeight;
              toast.success("Image uploaded successfully - will be saved separately");
            }
          })
          .catch((error) => {
            console.error("Error uploading image:", error);
            toast.error("Failed to upload image");
          });
        e.target.value = "";
      },
      [isReadOnly, processPastedImage],
    );

    const handleColorSelect = useCallback(
      (color) => {
        if (isReadOnly) return;
        if (color) {
          document.execCommand("foreColor", false, color);
          setSelectedColor(color);
        } else {
          document.execCommand("removeFormat", false, null);
          setSelectedColor("#000000");
        }
        if (editorRef.current) setRichTextContent(editorRef.current.innerHTML);
        setShowColorPicker(false);
        if (editorRef.current) editorRef.current.focus();
      },
      [isReadOnly],
    );

    // ----- Document handling (memoized) -----
    const isValidFileType = useCallback((file) => Object.keys(ALLOWED_FILE_TYPES).includes(file.type), [ALLOWED_FILE_TYPES]);

    const handleAddDocument = useCallback(() => {
      if (isReadOnly) return;
      setCurrentDocumentBeingEdited(null);
      setNewDocument({ name: "", file: null });
      setErrors({});
      setShowDocumentModal(true);
    }, [isReadOnly]);

    const handleEditDocument = useCallback(
      (doc) => {
        if (isReadOnly) return;
        setCurrentDocumentBeingEdited(doc);
        setNewDocument({ name: doc.name, file: null });
        setErrors({});
        setShowDocumentModal(true);
      },
      [isReadOnly],
    );

    const handleDeleteDocument = useCallback(
      (indexToDelete) => {
        if (isReadOnly) return;
        if (window.confirm("Are you sure you want to remove this document from the list? It will not be saved.")) {
          setDocuments((prev) => prev.filter((_, i) => i !== indexToDelete));
        }
      },
      [isReadOnly],
    );

    const handleDocumentFileChange = useCallback(
      (e) => {
        if (isReadOnly) return;
        const file = e.target.files[0];
        setError("");
        if (!isValidFileType(file)) {
          setError("Invalid file type!");
          return;
        }
        setNewDocument((prev) => ({ ...prev, file }));
      },
      [isReadOnly, isValidFileType],
    );

    const handleDocumentSubmit = useCallback(() => {
      if (isReadOnly) return;
      if (!newDocument.name.trim()) {
        setError("Document name is required.");
        return;
      }
      if (!currentDocumentBeingEdited && !newDocument.file) {
        setError("Please select a file to add.");
        return;
      }
      const docToStage = {
        id: currentDocumentBeingEdited?.id || `temp-${Date.now()}`,
        name: newDocument.name.trim(),
        file: newDocument.file || currentDocumentBeingEdited?.file,
        fileName: newDocument.file?.name || currentDocumentBeingEdited?.fileName || "N/A",
      };
      if (currentDocumentBeingEdited) {
        setDocuments((prev) =>
          prev.map((doc) => (doc.id === currentDocumentBeingEdited.id ? { ...doc, ...docToStage } : doc)),
        );
      } else {
        setDocuments((prev) => [...prev, docToStage]);
      }
      setShowDocumentModal(false);
      setNewDocument({ name: "", file: null });
      setCurrentDocumentBeingEdited(null);
      setErrors({});
    }, [isReadOnly, newDocument.name, newDocument.file, currentDocumentBeingEdited]);

    // ----- Priority handling -----
    const handlePriorityClick = useCallback(() => {
      if (isReadOnly) return;
      let nextPriority;
      if (selectedPriority === "1") nextPriority = "3";
      else if (selectedPriority === "3") nextPriority = "4";
      else if (selectedPriority === "4") nextPriority = "5";
      else nextPriority = "1";
      setSelectedPriority(nextPriority);
      let priorityText = nextPriority === "5" ? "Completed" : nextPriority === "4" ? "High" : nextPriority === "3" ? "Medium" : "No Priority";
      toast.success(`Priority set to ${priorityText}`);
    }, [isReadOnly, selectedPriority]);

    // ----- Save note logic (memoized) -----
    const prepareNoteContent = useCallback(() => {
      if (isReadOnly) return "";
      if (pastedImages.length === 0) {
        let cleanContent = richTextContent;
        if (cleanContent) {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = cleanContent;
          const images = tempDiv.querySelectorAll("img");
          images.forEach((img) => img.remove());
          cleanContent = cleanHtml(cleanContent);
        }
        return cleanContent || "";
      }
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = richTextContent;
      const images = tempDiv.querySelectorAll("img[data-image-id]");
      images.forEach((img) => img.remove());
      let cleanContent = tempDiv.innerHTML;
      cleanContent = cleanHtml(cleanContent);
      return cleanContent;
    }, [isReadOnly, pastedImages.length, richTextContent]);

    const cleanHtml = useCallback((htmlContent) => {
      if (!htmlContent || htmlContent.trim() === "") return "";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const emptyElements = tempDiv.querySelectorAll("div:empty, p:empty, span:empty, br:only-child");
      emptyElements.forEach((el) => {
        if (!el.textContent.trim() && !el.querySelector("*")) el.remove();
      });
      const blockElements = tempDiv.querySelectorAll("div, p");
      blockElements.forEach((block) => {
        let firstChild = block.firstChild;
        while (
          firstChild &&
          ((firstChild.nodeType === Node.ELEMENT_NODE && firstChild.tagName === "BR") ||
            (firstChild.nodeType === Node.TEXT_NODE && !firstChild.textContent.trim()))
        ) {
          const nextSibling = firstChild.nextSibling;
          firstChild.remove();
          firstChild = nextSibling;
        }
        let lastChild = block.lastChild;
        while (
          lastChild &&
          ((lastChild.nodeType === Node.ELEMENT_NODE && lastChild.tagName === "BR") ||
            (lastChild.nodeType === Node.TEXT_NODE && !lastChild.textContent.trim()))
        ) {
          const previousSibling = lastChild.previousSibling;
          lastChild.remove();
          lastChild = previousSibling;
        }
        const brElements = block.querySelectorAll("br");
        brElements.forEach((br, index) => {
          if (br.nextSibling && br.nextSibling.nodeType === Node.ELEMENT_NODE && br.nextSibling.tagName === "BR")
            br.remove();
        });
      });
      const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (node.textContent.trim() ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_ACCEPT),
      });
      const emptyTextNodes = [];
      let node;
      while ((node = walker.nextNode())) emptyTextNodes.push(node);
      emptyTextNodes.forEach((node) => node.remove());
      let cleanedHtml = tempDiv.innerHTML;
      cleanedHtml = cleanedHtml
        .replace(/\s+/g, " ")
        .replace(/>\s+</g, "><")
        .replace(/\s+$/g, "")
        .replace(/^\s+/g, "")
        .trim();
      cleanedHtml = cleanedHtml
        .replace(/<div><\/div>/g, "")
        .replace(/<p><\/p>/g, "")
        .replace(/<span><\/span>/g, "")
        .replace(/<div>\s*<\/div>/g, "")
        .replace(/<p>\s*<\/p>/g, "")
        .replace(/<span>\s*<\/span>/g, "");
      return cleanedHtml;
    }, []);

    const uploadDocumentWithSignalR = useCallback(
      async (doc, siteNoteId, userId) => {
        if (!doc.file) throw new Error("No file selected for upload");
        const formData = new FormData();
        formData.append("Name", doc.name);
        formData.append("File", doc.file);
        formData.append("SiteNoteId", siteNoteId);
        formData.append("UserId", userId);
        const headers = {};
        if (isSignalRConnected && connectionRef.current?.connectionId) {
          headers["X-Connection-Id"] = connectionRef.current.connectionId;
        }
        const response = await fetch(`${apiUrl}/Documents/AddDocument`, {
          method: "POST",
          body: formData,
          headers,
        });
        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = "Upload failed";
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.errors?.Name?.[0] || errorMessage;
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }
        return await response.json();
      },
      [apiUrl, isSignalRConnected],
    );

    const uploadInlineImage = useCallback(
      async (doc, siteNoteId, userId) => {
        if (!doc.file) throw new Error("No file selected for upload");
        const formData = new FormData();
        formData.append("File", doc.file);
        formData.append("SiteNoteId", siteNoteId);
        formData.append("UserId", userId);
        if (doc.name) formData.append("Description", doc.name);
        const response = await fetch(`${apiUrl}/InlineImages/UploadInlineImage`, {
          method: "POST",
          body: formData,
        });
        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = "Image upload failed";
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.errors?.File?.[0] || errorMessage;
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }
        return await response.json();
      },
      [apiUrl],
    );

    const handleAddPriority = useCallback(
      async (noteId, userId) => {
        const response = await fetch(`${apiUrl}/Priority/AddPriority`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            noteID: noteId,
            priorityValue: selectedPriority,
            userId,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to save priority");
        }
      },
      [apiUrl, selectedPriority],
    );

const handleSaveJournal = useCallback(async () => {
  if (isReadOnly) return;
  
  const newErrors = {};
  if (!selectedJob) newErrors.job = "Please select a job";
  if (!selectedDate) newErrors.date = "Please select a date";
  const noteHtmlContent = prepareNoteContent();
  const hasTextContent = noteHtmlContent.replace(/<[^>]*>/g, "").trim();
  const hasImages = pastedImages.length > 0;
  if (!hasTextContent && !hasImages && !taskName.trim()) {
    newErrors.note = "Note content or task name is required";
  }
  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    return;
  }

  setIsSaving(true);
  setApiError(null);

  try {
    const user = getCurrentUser();
    const noteData = {
      note: noteHtmlContent,
      date: new Date(selectedDate).toISOString(),
      taskId: prefilledData?.taskid,
      userId: user.id,
    };

    // 1. Save the note first
    const savedNote = await addSiteNote(noteData);
    const siteNoteId = savedNote.siteNoteId;
    if (!siteNoteId) throw new Error("Failed to retrieve SiteNoteId from the saved note.");

    // 2. Upload all documents in parallel (not sequential)
    const documentUploadPromises = documents
      .filter(doc => doc.file)
      .map(doc => uploadDocumentWithSignalR(doc, siteNoteId, user.id).catch(err => {
        console.error(`Error uploading document "${doc.name}":`, err);
        toast.error(`Failed to upload document "${doc.name}": ${err.message}`);
        return null; // resolve with null to not break other uploads
      }));
    
    // 3. Upload all images in parallel
    const imageUploadPromises = pastedImages.map(async (image) => {
      try {
        const base64Response = await fetch(image.base64);
        const blob = await base64Response.blob();
        const file = new File([blob], image.name, { type: image.type });
        await uploadInlineImage({ name: image.name, file }, siteNoteId, user.id);
        return { success: true, name: image.name };
      } catch (err) {
        console.error(`Error uploading image "${image.name}":`, err);
        return { success: false, name: image.name, error: err.message };
      }
    });

    // Wait for all uploads to complete (don't block on document uploads, but wait for images)
    const [docResults, imageResults] = await Promise.all([
      Promise.all(documentUploadPromises),
      Promise.all(imageUploadPromises)
    ]);

    const failedImages = imageResults.filter(r => !r.success);
    if (failedImages.length > 0) {
      toast.error(`${failedImages.length} image(s) failed to upload`);
    }
    const successfulImages = imageResults.filter(r => r.success);
    if (successfulImages.length > 0) {
      toast.success(`${successfulImages.length} image(s) uploaded successfully`);
    }

    // 4. Add priority
    await handleAddPriority(siteNoteId, user.id);

    // 5. Construct new note object for UI updates
    const newNoteObject = {
      id: siteNoteId,
      note: noteHtmlContent,
      date: selectedDate,
      timeStamp: new Date().toISOString(),
      jobId: selectedJob,
      job: selectedJobData?.name || selectedJob,
      projectId: selectedProject,
      project: selectedProjectData?.name || selectedProject,
      workspaceId: selectedWorkspace,
      workspace: userworksaces.find(w => w.id.toString() === selectedWorkspace)?.name,
      userName: user.name || user.username,
      userId: user.id,
      documentCount: documents.length,
      inlineImageCount: pastedImages.length,
     
    };

    // 6. Refresh views – only one refresh based on priority
    if (viewMode === "stacked" && refreshStackedView) {
          await refreshStackedView(newNoteObject);
          await refreshNotes();
          if (taskId) {
            if (fetchNotesForTask) {
              await fetchNotesForTask(taskId, selectedJob);
              await fetchTasksForJob(selectedJob);
            }
          }
        } else if (hasActiveFilters && fetchNotesWithFilters) {
          await fetchNotesWithFilters(selectedFilters);
        } else if (hasActiveSearchText) {
          await performSearching(searchTerm);
        } else {
          await refreshNotes();
          if (taskId && fetchNotesForTask) {
            await fetchNotesForTask(taskId, selectedJob);
          }
        }


    onClose();
    toast.success("Note saved successfully!");
  } catch (error) {
    console.error("Save error:", error);
    setApiError(error.message || "Failed to save note");
    toast.error("Failed to save note");
  } finally {
    setIsSaving(false);
  }
}, [isReadOnly, selectedJob, selectedDate, prepareNoteContent, pastedImages, taskName, getCurrentUser, prefilledData, addSiteNote, documents, uploadDocumentWithSignalR, uploadInlineImage, handleAddPriority, selectedJobData, selectedProject, selectedProjectData, selectedWorkspace, userworksaces, viewMode, refreshStackedView, hasActiveFilters, fetchNotesWithFilters, selectedFilters, hasActiveSearchText, performSearching, searchTerm, refreshNotes, onClose]);

    // ----- Search logic -----
    const performSearch = useCallback(
      (query) => {
        if (!query.trim() || isReadOnly) {
          setSearchResults([]);
          setShowSearchResults(false);
          return;
        }
        setIsSearching(true);
        setTimeout(() => {
          try {
            const lowerCaseQuery = query.toLowerCase().trim();
            const filtered = allSearchData.filter((item) => {
              if (!item) return false;
              const searchText = `${item.workspaceName || ""} ${item.projectName || ""} ${item.jobName || ""} ${item.fullPath || ""}`.toLowerCase();
              return searchText.includes(lowerCaseQuery);
            });
            setSearchResults(filtered);
            setShowSearchResults(true);
          } catch (error) {
            console.error("Search error:", error);
            setSearchResults([]);
          } finally {
            setIsSearching(false);
          }
        }, 300);
      },
      [allSearchData, isReadOnly],
    );

    // Debounced search
    useEffect(() => {
      if (!isOpen) return;
      const timer = setTimeout(() => {
        if (searchQuery.trim() && !isReadOnly) performSearch(searchQuery);
        else {
          setSearchResults([]);
          setShowSearchResults(false);
        }
      }, 300);
      return () => clearTimeout(timer);
    }, [searchQuery, performSearch, isOpen, isReadOnly]);

    // Fetch all search data on open (memoized with cache)
    useEffect(() => {
      let isMounted = true;
      const fetchAllSearchData = async () => {
        if (!isOpen || isReadOnly) return;
        try {
          const user = getCurrentUser();
          const response = await fetch(`${apiUrl}/SiteNote/SearchJobs?userId=${user.id}&search=`);
          if (!response.ok) throw new Error(`Failed to fetch search data: ${response.status}`);
          const data = await response.json();
          if (isMounted) setAllSearchData(data.results || data || []);
        } catch (error) {
          console.error("Error fetching search data:", error);
          if (isMounted) setApiError("Failed to load search data");
        }
      };
      fetchAllSearchData();
      return () => { isMounted = false; };
    }, [isOpen, apiUrl, isReadOnly, getCurrentUser]);

    const handleSearchResultSelect = useCallback(
      async (result) => {
        if (!result || isReadOnly) return;
        setSelectedWorkspace("");
        setSelectedProject("");
        setSelectedJob("");
        setFilteredProjects([]);
        setFilteredJobs([]);
        setSelectedProjectData(null);
        setSelectedJobData(null);
        setApiError(null);
        try {
          if (result.workspaceId) {
            const matchingWorkspace = userworksaces.find(
              (workspace) => workspace && workspace.id && workspace.id.toString() === result.workspaceId.toString(),
            );
            if (matchingWorkspace) setSelectedWorkspace(matchingWorkspace.id.toString());
          }
          if (result.projectId) {
            setIsLoadingProjects(true);
            try {
              const projectResponse = await fetch(`${apiUrl}/Project/GetProjectById/${result.projectId}`);
              if (projectResponse.ok) {
                const projectData = await projectResponse.json();
                const normalizedProject = normalizeProjectData(projectData, result);
                if (normalizedProject) {
                  setSelectedProjectData(normalizedProject);
                  setSelectedProject(normalizedProject.id.toString());
                }
              }
            } catch (projectError) {
              const fallbackProject = normalizeProjectData(null, result);
              if (fallbackProject) {
                setSelectedProjectData(fallbackProject);
                setSelectedProject(fallbackProject.id.toString());
              }
            } finally {
              setIsLoadingProjects(false);
            }
          }
          if (result.jobId) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            setIsLoadingJobs(true);
            try {
              const jobResponse = await fetch(`${apiUrl}/Job/GetJobById/${result.jobId}`);
              if (jobResponse.ok) {
                const jobData = await jobResponse.json();
                const normalizedJob = normalizeJobData(jobData, result);
                if (normalizedJob) {
                  setSelectedJobData(normalizedJob);
                  setSelectedJob(normalizedJob.id.toString());
                }
              }
            } catch (jobError) {
              const fallbackJob = normalizeJobData(null, result);
              if (fallbackJob) {
                setSelectedJobData(fallbackJob);
                setSelectedJob(fallbackJob.id.toString());
              }
            } finally {
              setIsLoadingJobs(false);
            }
          }
        } catch (error) {
          console.error("Error in search result selection:", error);
          setApiError(`Failed to load selection: ${error.message}`);
        }
        setSearchQuery("");
        setShowSearchResults(false);
        setSearchResults([]);
        setTimeout(() => {
          if (editorRef.current && !isReadOnly) editorRef.current.focus();
        }, 500);
      },
      [isReadOnly, userworksaces, apiUrl, normalizeProjectData, normalizeJobData],
    );

    // ----- Effects for fetching projects/jobs -----
    useEffect(() => {
      if (isReadOnly) return;
      const fetchProjectsByWorkspace = async () => {
        if (prefilledData?.projectId && selectedWorkspace === prefilledData.workspaceId?.toString()) return;
        if (!selectedWorkspace) {
          setFilteredProjects([]);
          setSelectedProject("");
          setSelectedProjectData(null);
          return;
        }
        setIsLoadingProjects(true);
        try {
          const user = getCurrentUser();
          const response = await fetch(`${apiUrl}/Project/GetProjectsByUserJobPermission/${user.id}/${selectedWorkspace}`);
          if (!response.ok) throw new Error("Failed to fetch projects");
          const data = await response.json();
          setFilteredProjects(data.projects || []);
        } catch (error) {
          console.error("Error fetching projects:", error);
          setApiError("Failed to load projects");
          setFilteredProjects([]);
        } finally {
          setIsLoadingProjects(false);
        }
      };
      fetchProjectsByWorkspace();
    }, [selectedWorkspace, apiUrl, prefilledData, isReadOnly, getCurrentUser]);

    useEffect(() => {
      if (isReadOnly) return;
      const fetchJobsByProject = async () => {
        if (prefilledData?.jobId && selectedProject === prefilledData.projectId?.toString()) return;
        if (!selectedProject) {
          setFilteredJobs([]);
          setSelectedJob("");
          setSelectedJobData(null);
          return;
        }
        setIsLoadingJobs(true);
        try {
          const user = getCurrentUser();
          const response = await fetch(`${apiUrl}/UserJobAuth/GetJobsByUserAndProject/${user.id}/${selectedProject}`);
          if (!response.ok) throw new Error("Failed to fetch jobs");
          const data = await response.json();
          setFilteredJobs(data.jobs || []);
        } catch (error) {
          console.error("Error fetching jobs:", error);
          setApiError("Failed to load jobs");
          setFilteredJobs([]);
        } finally {
          setIsLoadingJobs(false);
        }
      };
      fetchJobsByProject();
    }, [selectedProject, apiUrl, prefilledData, isReadOnly, getCurrentUser]);

    // ----- Initialize modal when opened -----
    useEffect(() => {
      if (isOpen) {
        const today = new Date();
        const formattedDate = today.toISOString().split("T")[0];
        setSelectedDate(prefilledData?.date || formattedDate);
        setActiveTab("journal");
        setSelectedPriority("1");
        if (!prefilledData) {
          setSelectedWorkspace("");
          setSelectedProject("");
          setSelectedJob("");
          setRichTextContent("");
          setFilteredProjects([]);
          setFilteredJobs([]);
          setSelectedProjectData(null);
          setSelectedJobData(null);
        }
        setDocuments([]);
        setNewDocument({ name: "", file: null });
        setShowDocumentModal(false);
        setCurrentDocumentBeingEdited(null);
        setErrors({});
        setIsSaving(false);
        setApiError(null);
        setPastedImages([]);
        setShowEmojiPicker(false);
      }
    }, [isOpen, prefilledData]);

    // Handle prefilled data
    useEffect(() => {
      if (isOpen && prefilledData && !isReadOnly) {
        if (prefilledData.date) setSelectedDate(prefilledData.date);
        if (prefilledData.workspaceId) setSelectedWorkspace(prefilledData.workspaceId.toString());
        else if (prefilledData.workspace) {
          const workspace = userworksaces.find(
            (w) => w && w.name && (w.name === prefilledData.workspace || w.text === prefilledData.workspace),
          );
          if (workspace) setSelectedWorkspace(workspace.id.toString());
        }
        if (prefilledData.projectId) {
          const projectData = {
            id: prefilledData.projectId,
            projectId: prefilledData.projectId,
            name: prefilledData.project || "Project",
            workspaceId: prefilledData.workspaceId,
          };
          setSelectedProjectData(projectData);
          setSelectedProject(prefilledData.projectId.toString());
        }
        if (prefilledData.jobId) {
          const jobData = {
            id: prefilledData.jobId,
            jobId: prefilledData.jobId,
            name: prefilledData.job || "Job",
            projectId: prefilledData.projectId,
          };
          setSelectedJobData(jobData);
          setSelectedJob(prefilledData.jobId.toString());
        }
        if (prefilledData.taskName) setTaskName(prefilledData.taskName);
        else if (prefilledData.task) setTaskName(prefilledData.task);
        else if (prefilledData.title) setTaskName(prefilledData.title);
      }
    }, [isOpen, prefilledData, userworksaces, isReadOnly]);

    // SignalR connection
    useEffect(() => {
      if (isOpen && !isReadOnly) {
        const connectSignalR = async () => {
          try {
            const connection = new signalR.HubConnectionBuilder()
              .withUrl(`${process.env.REACT_APP_API_BASE_URL}/hubs/uploadprogress`)
              .withAutomaticReconnect()
              .build();
            await connection.start();
            connectionRef.current = connection;
            setIsSignalRConnected(true);
            connection.on("ReceiveProgress", (progress) => console.log("Upload progress:", progress));
          } catch (err) {
            console.warn("SignalR connection failed, continuing without upload progress", err);
            setIsSignalRConnected(false);
          }
        };
        connectSignalR();
        return () => {
          if (connectionRef.current) {
            connectionRef.current.stop();
            connectionRef.current = null;
            setIsSignalRConnected(false);
          }
        };
      }
    }, [isOpen, isReadOnly]);

    // Emoji picker setup
    useEffect(() => {
      if (isOpen && !isReadOnly && emojiPickerRef.current) {
        const handleEmojiClick = (event) => {
          if (!editorRef.current || isReadOnly) return;
          let emojiChar = "";
          if (event.detail) {
            if (typeof event.detail === "string") emojiChar = event.detail;
            else if (event.detail.unicode) emojiChar = event.detail.unicode;
            else if (event.detail.native) emojiChar = event.detail.native;
            else if (event.detail.emoji) emojiChar = event.detail.emoji;
          }
          if (!emojiChar && event.target) {
            if (event.target.getAttribute("data-emoji")) emojiChar = event.target.getAttribute("data-emoji");
            else if (event.target.textContent) {
              const emojiRegex = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
              const match = event.target.textContent.trim().match(emojiRegex);
              if (match) emojiChar = match[0];
            }
          }
          if (!emojiChar) emojiChar = "❓";
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const emojiNode = document.createTextNode(emojiChar);
            range.deleteContents();
            range.insertNode(emojiNode);
            range.setStartAfter(emojiNode);
            range.setEndAfter(emojiNode);
            selection.removeAllRanges();
            selection.addRange(range);
            setRichTextContent(editorRef.current.innerHTML);
          }
          setShowEmojiPicker(false);
          if (editorRef.current) editorRef.current.focus();
        };
        emojiPickerRef.current.addEventListener("emoji-click", handleEmojiClick);
        const handleClickOutside = (event) => {
          if (showEmojiPicker && emojiButtonRef.current && !emojiButtonRef.current.contains(event.target) && emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
            setShowEmojiPicker(false);
          }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          emojiPickerRef.current?.removeEventListener("emoji-click", handleEmojiClick);
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen, showEmojiPicker, isReadOnly]);

    // Focus for grid source
    useEffect(() => {
      if (isOpen && source === "grid" && !hasFocusedRef.current && !isReadOnly) {
        const focusEditor = () => {
          if (editorRef.current && document.contains(editorRef.current)) {
            editorRef.current.focus();
            hasFocusedRef.current = true;
            return true;
          }
          return false;
        };
        const timeouts = [200, 400, 600].map((delay) => setTimeout(focusEditor, delay));
        return () => timeouts.forEach(clearTimeout);
      }
    }, [isOpen, source, isReadOnly]);

    // Reset on close
    useEffect(() => {
      if (!isOpen) {
        hasFocusedRef.current = false;
        setSearchQuery("");
        setSearchResults([]);
        setShowSearchResults(false);
        setSelectedProjectData(null);
        setSelectedJobData(null);
        setApiError(null);
        setPastedImages([]);
        setRichTextContent("");
        setShowEmojiPicker(false);
        setShowColorPicker(false);
        setTaskName("");
      }
    }, [isOpen]);

    // Keyboard shortcut for save
    const handleSaveShortcut = useCallback(
      (event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === "s" && !isSaving && !isReadOnly) {
          event.preventDefault();
          handleSaveJournal();
        }
      },
      [isSaving, handleSaveJournal, isReadOnly],
    );

    useEffect(() => {
      if (isOpen) {
        document.addEventListener("keydown", handleSaveShortcut);
        return () => document.removeEventListener("keydown", handleSaveShortcut);
      }
    }, [isOpen, handleSaveShortcut]);

    // Scroll to errors
    useEffect(() => {
      if (!isOpen) return;
      const hasErrors = apiError || (errors && Object.keys(errors).length > 0);
      if (hasErrors && modalRef.current) {
        try {
          if (typeof modalRef.current.scrollTo === "function") modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
          else modalRef.current.scrollTop = 0;
        } catch (e) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }, [apiError, errors, isOpen]);

    // Click outside for color picker
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (showColorPicker && colorPickerRef.current && !colorPickerRef.current.contains(event.target) && !event.target.closest(".color-button")) {
          setShowColorPicker(false);
        }
      };
      if (showColorPicker) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }
    }, [showColorPicker]);

    // Dragging logic
    const handleMouseDown = useCallback(
      (e) => {
        const isDragHandle = e.target.closest(".modal-drag-handle");
        if (!isDragHandle) return;
        e.preventDefault();
        setIsDragging(true);
        const modalRect = modalRef.current.getBoundingClientRect();
        const offsetX = e.clientX - modalRect.left;
        const offsetY = e.clientY - modalRect.top;
        setDragOffset({ x: offsetX, y: offsetY });
        setHasUserDragged(true);
      },
      [],
    );

    const handleMouseMove = useCallback(
      (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.clientX - dragOffset.x;
        const y = e.clientY - dragOffset.y;
        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const boundedX = Math.max(0, Math.min(x, windowWidth - modalWidth));
        const boundedY = Math.max(0, Math.min(y, windowHeight - modalHeight));
        setModalPosition({ x: boundedX, y: boundedY });
      },
      [isDragging, dragOffset],
    );

    const handleMouseUp = useCallback(() => setIsDragging(false), []);
    const handleTouchStart = useCallback(
      (e) => {
        const touch = e.touches[0];
        const isDragHandle = e.target.closest(".modal-drag-handle");
        if (!isDragHandle) return;
        e.preventDefault();
        setIsDragging(true);
        const modalRect = modalRef.current.getBoundingClientRect();
        const offsetX = touch.clientX - modalRect.left;
        const offsetY = touch.clientY - modalRect.top;
        setDragOffset({ x: offsetX, y: offsetY });
        setHasUserDragged(true);
      },
      [],
    );
    const handleTouchMove = useCallback(
      (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const touch = e.touches[0];
        const x = touch.clientX - dragOffset.x;
        const y = touch.clientY - dragOffset.y;
        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const boundedX = Math.max(0, Math.min(x, windowWidth - modalWidth));
        const boundedY = Math.max(0, Math.min(y, windowHeight - modalHeight));
        setModalPosition({ x: boundedX, y: boundedY });
      },
      [isDragging, dragOffset],
    );
    const handleTouchEnd = useCallback(() => setIsDragging(false), []);
    const handleHeaderDoubleClick = useCallback(() => {
      if (modalRef.current) {
        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const x = (windowWidth - modalWidth) / 2;
        const y = Math.max(20, (windowHeight - modalHeight) / 4);
        setModalPosition({ x, y });
        setHasUserDragged(false);
        toast.success("Modal centered");
      }
    }, []);

    useEffect(() => {
      if (isOpen && modalRef.current) {
        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const x = (windowWidth - modalWidth) / 2;
        const y = Math.max(20, (windowHeight - modalHeight) / 4);
        setModalPosition({ x, y });
      }
    }, [isOpen]);

    useEffect(() => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("touchmove", handleTouchMove, { passive: false });
        document.addEventListener("touchend", handleTouchEnd);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
      } else {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    // Expose focus method
    useImperativeHandle(ref, () => ({
      focusTextarea: () => {
        if (editorRef.current) {
          const focus = () => editorRef.current.focus();
          setTimeout(focus, 100);
          setTimeout(focus, 300);
          setTimeout(focus, 500);
        }
      },
    }));

    // ----- Render helper functions (memoized JSX) -----
    const renderSearchSection = useMemo(() => {
      if (isReadOnly) return null;
      return (
        <div className="search-section">
          <div className="form-group full-width search-container" ref={searchInputRef}>
            <label>🔍 Quick Search</label>
            <div className="search-input-wrapper" style={{ width: "100%" }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type to search for workspace, project, or job..."
                className="search-input"
                style={{ width: "100%" }}
              />
              {isSearching && <div className="search-spinner">🔍 Searching...</div>}
            </div>
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown" style={{ width: "100%" }}>
                <div className="search-results-header">
                  Found {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{searchQuery}"
                </div>
                {searchResults.map((result, index) => (
                  <div
                    key={`${result.workspaceId}-${result.projectId}-${result.jobId}-${index}`}
                    className="search-result-item"
                    onClick={() => handleSearchResultSelect(result)}
                  >
                    <div className="search-result-fullpath">
                      {result.fullPath || `${result.workspaceName} → ${result.projectName} → ${result.jobName}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showSearchResults && searchQuery && searchResults.length === 0 && !isSearching && (
              <div className="search-results-dropdown" style={{ width: "100%" }}>
                <div className="search-no-results">
                  No results found for "<strong>{searchQuery}</strong>"
                  <div style={{ fontSize: "11px", marginTop: "4px" }}>Try searching with different keywords</div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }, [isReadOnly, searchQuery, isSearching, showSearchResults, searchResults, handleSearchResultSelect]);

    const renderEditorToolbar = useMemo(() => {
      if (isReadOnly) return null;
      return (
        <div className="editor-toolbar">
          <div className="editor-formatting-tools">
            <button onClick={() => formatText("bold")} title="Bold">
              <i className="fas fa-bold" />
            </button>
            <button onClick={() => formatText("italic")} title="Italic">
              <i className="fas fa-italic" />
            </button>
            <button onClick={() => formatText("underline")} title="Underline">
              <i className="fas fa-underline" />
            </button>
            <div className="color-picker-wrapper" style={{ position: "relative", display: "inline-block" }}>
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                title="Text Color"
                className={`color-button ${showColorPicker ? "active" : ""}`}
                type="button"
                style={{ border: `2px solid ${selectedColor}`, backgroundColor: "white" }}
              >
                <i className="fas fa-palette" style={{ color: selectedColor }} />
              </button>
              {showColorPicker && (
                <div className="color-picker-dropdown" ref={colorPickerRef}>
                  <div className="color-picker-header">
                    <span>Text Color</span>
                    <button onClick={() => setShowColorPicker(false)} className="close-color-picker">×</button>
                  </div>
                  <div className="color-grid">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        className={`color-option ${selectedColor === color.value ? "selected" : ""}`}
                        style={{ backgroundColor: color.value, border: selectedColor === color.value ? "3px solid #333" : "2px solid transparent" }}
                        onClick={() => handleColorSelect(color.value)}
                        title={color.name}
                      />
                    ))}
                  </div>
                  <div className="color-picker-footer">
                    <button className="remove-color-button" onClick={() => handleColorSelect(null)}>Remove Color</button>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => formatText("insertUnorderedList")} title="Bullet List">
              <i className="fas fa-list-ul" />
            </button>
            <button onClick={() => formatText("insertOrderedList")} title="Numbered List">
              <i className="fas fa-list-ol" />
            </button>
            <div className="emoji-picker-wrapper" style={{ position: "relative" }}>
              <button
                ref={emojiButtonRef}
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                title="Insert Emoji"
                className={`emoji-button ${showEmojiPicker ? "active" : ""}`}
                type="button"
              >
                <i className="far fa-smile" />
              </button>
              {showEmojiPicker && (
                <div className="emoji-picker-container">
                  <emoji-picker
                    ref={emojiPickerRef}
                    class="emoji-picker"
                    style={{
                      "--background": "#ffffff",
                      "--border-color": "#e0e0e0",
                      "--border-radius": "8px",
                      "--button-active-background": "#f0f0f0",
                      "--button-hover-background": "#f5f5f5",
                      "--category-emoji-padding": "8px",
                      "--category-emoji-size": "24px",
                      "--category-font-color": "#666",
                      "--category-font-size": "13px",
                      "--indicator-color": "#007bff",
                      "--input-border-color": "#e0e0e0",
                      "--input-border-radius": "20px",
                      "--input-font-color": "#333",
                      "--input-placeholder-color": "#999",
                      "--num-columns": "8",
                      "--outline-color": "#007bff80",
                      width: "350px",
                      height: "400px",
                    }}
                  />
                </div>
              )}
            </div>
            <div className="image-upload-wrapper">
              <label htmlFor="image-upload" className="image-upload-button" title="Upload Image">
                <i className="fas fa-image" />
                <input id="image-upload" type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} />
              </label>
            </div>
            <div className="documents-button-wrapper" style={{ position: "relative" }}>
              <button
                onClick={() => setActiveTab("documents")}
                title={`${documents.length} document${documents.length !== 1 ? "s" : ""} attached`}
                className={`documents-button ${activeTab === "documents" ? "active" : ""}`}
              >
                <i className="fas fa-paperclip" />
                {documents.length > 0 && <span className="documents-badge">{documents.length}</span>}
              </button>
            </div>
            <div className="priority-flag-container">
              <button
                onClick={handlePriorityClick}
                title={selectedPriority === "1" ? "No Priority - Click to set" : selectedPriority === "3" ? "Medium Priority - Click to change" : selectedPriority === "4" ? "High Priority - Click to change" : selectedPriority === "5" ? "Priority Level 5 (Green) - Click to change" : "Click to set priority"}
                className={`priority-flag-button priority-${selectedPriority} ${selectedPriority > 1 ? "has-priority" : ""}`}
              >
                <i className="fas fa-flag" />
                {selectedPriority > 1 && <div className={`priority-flag-dot priority-${selectedPriority}`} />}
              </button>
            </div>
            <button onClick={clearEditor} title="Clear All" className="clear-button">
              <i className="fas fa-trash" />
            </button>
          </div>
          {pastedImages.length > 0 && (
            <div className="image-counter">
              <i className="fas fa-images" /> {pastedImages.length} image{pastedImages.length !== 1 ? "s" : ""} attached
            </div>
          )}
        </div>
      );
    }, [isReadOnly, formatText, showColorPicker, selectedColor, colorOptions, handleColorSelect, showEmojiPicker, handleImageUpload, documents.length, activeTab, handlePriorityClick, selectedPriority, pastedImages.length, clearEditor]);

    const renderJournalTab = useMemo(() => {
      return (
        <div className="journal-section">
          <div className="form-group">
            <label>
              Date {errors.date && <span className="error-message-inline">{errors.date}</span>}
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setErrors((prev) => ({ ...prev, date: undefined }));
              }}
              disabled={isReadOnly}
            />
          </div>
          <div className="editor-content-container">
            <div className="form-group">
              <label>
                Note {errors.note && <span className="error-message-inline">{errors.note}</span>}
              </label>
              {!isReadOnly && renderEditorToolbar}
              <div
                ref={editorRef}
                contentEditable={!isReadOnly}
                className={`rich-text-editor ${isReadOnly ? "read-only" : ""}`}
                onPaste={handlePaste}
                onInput={handleEditorInput}
                placeholder={isReadOnly ? "Read-only mode - Cannot edit" : "Type your note here and paste images."}
                suppressContentEditableWarning={true}
                style={{
                  pointerEvents: isReadOnly ? "none" : "auto",
                  opacity: isReadOnly ? 0.7 : 1,
                  backgroundColor: isReadOnly ? "#f5f5f5" : "#fff",
                  cursor: isReadOnly ? "not-allowed" : "text",
                }}
              />
              {pastedImages.length > 0 && !isReadOnly && (
                <div className="image-upload-status">
                  <i className="fas fa-images" /> {pastedImages.length} image{pastedImages.length !== 1 ? "s" : ""} attached (will be saved separately)
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }, [errors.date, errors.note, selectedDate, isReadOnly, renderEditorToolbar, handlePaste, handleEditorInput, pastedImages.length]);

    const renderDocumentsTab = useMemo(() => {
      return (
        <div className="documents-section">
          <h3>Attached Documents</h3>
          {!isReadOnly && (
            <div className="document-actions">
              <button className="add-button" onClick={handleAddDocument}>Add Document</button>
            </div>
          )}
          <div className="documents-list">
            {documents.length === 0 ? (
              <p>No documents attached</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>File Name</th>
                    {!isReadOnly && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <tr key={doc.id || index}>
                      <td>{doc.name}</td>
                      <td>{doc.fileName || "N/A"}</td>
                      {!isReadOnly && (
                        <td className="document-actions-cell">
                          <button onClick={() => handleEditDocument(doc)} className="edit-button">Edit</button>
                          <button onClick={() => handleDeleteDocument(index)} className="delete-button">Delete</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      );
    }, [isReadOnly, documents, handleAddDocument, handleEditDocument, handleDeleteDocument]);

    const renderPriorityTab = useMemo(() => {
      return (
        <div className="journal-section">
          <div className="form-group">
            <label>Priority {errors.priority && <span className="error-message-inline">{errors.priority}</span>}</label>
            <div className="priority-visual-selector">
              <div
                className={`priority-option ${selectedPriority === "4" ? "selected" : ""} priority-high`}
                onClick={() => !isReadOnly && setSelectedPriority("4")}
                title="High Priority"
                style={{ cursor: isReadOnly ? "not-allowed" : "pointer" }}
              >
                <div className="priority-dot" />
                <span>High</span>
              </div>
              <div
                className={`priority-option ${selectedPriority === "3" ? "selected" : ""} priority-medium`}
                onClick={() => !isReadOnly && setSelectedPriority("3")}
                title="Medium Priority"
                style={{ cursor: isReadOnly ? "not-allowed" : "pointer" }}
              >
                <div className="priority-dot" />
                <span>Medium</span>
              </div>
              <div
                className={`priority-option ${selectedPriority === "1" ? "selected" : ""} priority-none`}
                onClick={() => !isReadOnly && setSelectedPriority("1")}
                title="No Priority"
                style={{ cursor: isReadOnly ? "not-allowed" : "pointer" }}
              >
                <div className="priority-dot" />
                <span>No Priority</span>
              </div>
            </div>
            <div className="priority-description">
              {selectedPriority === "4" && <span style={{ color: "#dc3545" }}><i className="fas fa-exclamation-circle" /> High priority - Needs immediate attention</span>}
              {selectedPriority === "3" && <span style={{ color: "#ffc107" }}><i className="fas fa-exclamation-triangle" /> Medium priority - Important but not urgent</span>}
              {selectedPriority === "1" && <span style={{ color: "#6c757d" }}><i className="fas fa-info-circle" /> No priority - Standard note</span>}
            </div>
          </div>
        </div>
      );
    }, [selectedPriority, errors.priority, isReadOnly]);

    const tabContent = useMemo(() => {
      switch (activeTab) {
        case "journal": return renderJournalTab;
        case "documents": return renderDocumentsTab;
        case "priority": return renderPriorityTab;
        default: return renderJournalTab;
      }
    }, [activeTab, renderJournalTab, renderDocumentsTab, renderPriorityTab]);

    return (
      <div className="reply-modal-overlay new-note-modal">
        <div
          className="modal-content"
          ref={modalRef}
          style={{
            position: "fixed",
            left: `${modalPosition.x}px`,
            top: `${modalPosition.y}px`,
            transform: "none",
            margin: 0,
            cursor: isDragging ? "grabbing" : "default",
            touchAction: "none",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            width: "90vw",
            maxWidth: "800px",
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
          }}
        >
          <div className="modal-content-wrapper">
            {prefilledData && (
              <div
                className="original-note-preview compact modal-drag-handle"
                style={{
                  cursor: isDragging ? "grabbing" : "grab",
                  userSelect: "none",
                  margin: "20px",
                  padding: "15px",
                  background: "#f8f9fa",
                  borderRadius: "6px",
                  border: "1px solid #e0e0e0",
                  flexShrink: 0,
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onDoubleClick={handleHeaderDoubleClick}
                title="Drag to move, double-click to center"
              >
                <div className="original-note-header">
                  <div className="original-note-user-info">
                    <div className="original-note-user">
                      <i className="fas fa-user-circle" />
                      <span>{getCurrentUser().name || "User"}</span>
                    </div>
                    <div className="original-note-date">
                      <i className="far fa-clock" />
                      <span>{formatDate(selectedDate)}</span>
                    </div>
                  </div>
                  <div className="original-note-context">
                    <span className="context-item">
                      <i className="fas fa-building" />
                      {selectedWorkspaceData?.name || prefilledData?.workspace || (prefilledData?.workspaceId ? `Workspace ${prefilledData.workspaceId}` : "—")}
                    </span>
                    <span className="context-item">
                      <i className="fas fa-project-diagram" /> {selectedProjectData?.name || "—"}
                    </span>
                    <span className="context-item">
                      <i className="fas fa-tasks" /> {selectedJobData?.name || "—"}
                    </span>
                    {taskName && (
                      <span className="context-item">
                        <i className="fas fa-check-circle" /> {taskName}
                      </span>
                    )}
                  </div>
                </div>
                {richTextContent && (
                  <div className="original-note-content">
                    <div className="original-note-text" dangerouslySetInnerHTML={safelyRenderHTML(richTextContent)} />
                  </div>
                )}
              </div>
            )}

            {!prefilledData && (
              <div
                className="modal-header modal-drag-handle"
                style={{
                  cursor: isDragging ? "grabbing" : "grab",
                  padding: "20px",
                  borderBottom: "1px solid #e0e0e0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onDoubleClick={handleHeaderDoubleClick}
              >
                <h2>
                  New Note{" "}
                  {isReadOnly && (
                    <span style={{ fontSize: "14px", color: "#666", marginLeft: "10px" }}>(Read-Only)</span>
                  )}
                </h2>
                <button className="close-button" onClick={onClose} disabled={isSaving}>×</button>
              </div>
            )}

            {apiError && (
              <div className="error-message sticky-error">
                {apiError}
                <button onClick={() => setApiError(null)} className="dismiss-error">×</button>
              </div>
            )}

            <div className="tabs-container" style={{ flexShrink: 0 }} />
            <div className="tab-content-scrollable" style={{ flex: 1, overflowY: "auto", padding: "20px", minHeight: 0 }}>
              {tabContent}
            </div>

            <div className="modal-footer sticky-footer" style={{ flexShrink: 0, padding: "16px 20px", borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button className="cancel-button" onClick={onClose} disabled={isSaving}>Cancel</button>
              {!isReadOnly && (
                <button className="save-button" onClick={handleSaveJournal} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>

          {showDocumentModal && !isReadOnly && (
            <div className="document-modal-overlay">
              <div className="document-modal" style={{ maxWidth: "560px" }}>
                <div className="modal-header">
                  <h3>{currentDocumentBeingEdited ? "Edit Document" : "Add Document"}</h3>
                  <button className="close-button" onClick={() => setShowDocumentModal(false)}>×</button>
                </div>
                {error && (
                  <div className="error-message" style={{ background: "#ffebee", padding: "12px", borderRadius: "6px", margin: "10px 20px" }}>
                    {error}
                    <button onClick={() => setError("")} style={{ float: "right", background: "none", border: "none", fontWeight: "bold" }}>×</button>
                  </div>
                )}
                <div style={{ padding: "20px" }}>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>Document Name:</label>
                    <input
                      type="text"
                      value={newDocument.name}
                      onChange={(e) => setNewDocument((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Site Photos March 2025"
                      style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                    />
                    {errors && errors.name && <div className="file-error">{errors.name}</div>}
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>File:</label>
                    <input id="newNoteFileInput" type="file" onChange={handleDocumentFileChange} style={{ width: "100%" }} />
                    {errors && errors.newDocumentFile && <div className="file-error">{errors.newDocumentFile}</div>}
                    {newDocument.file && (
                      <div style={{ marginTop: "12px", padding: "14px 16px", background: "#e8f5e8", border: "1px solid #4caf50", borderRadius: "8px", color: "#2e7d32", fontWeight: "500" }}>
                        Selected: <strong>{newDocument.file.name}</strong>
                        <span style={{ marginLeft: "10px", opacity: 0.8 }}>({(newDocument.file.size / 1024 / 1024).toFixed(1)} MB)</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-actions" style={{ padding: "16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                  <button onClick={() => { setShowDocumentModal(false); setNewDocument({ name: "", file: null }); setCurrentDocumentBeingEdited(null); }} style={{ padding: "10px 16px", border: "1px solid #ccc", borderRadius: "6px" }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleDocumentSubmit}
                    disabled={!newDocument.name.trim() && !currentDocumentBeingEdited}
                    style={{ padding: "10px 20px", background: "#1976d2", color: "white", border: "none", borderRadius: "6px", fontWeight: "600" }}
                  >
                    {currentDocumentBeingEdited ? "Save" : "Add Document"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

NewNoteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refreshNotes: PropTypes.func.isRequired,
  addSiteNote: PropTypes.func.isRequired,
  onUploadDocument: PropTypes.func.isRequired,
  onDeleteDocument: PropTypes.func,
  projects: PropTypes.array,
  jobs: PropTypes.array,
  prefilledData: PropTypes.object,
  defWorkSpaceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userworksaces: PropTypes.array,
  source: PropTypes.string,
  defaultWorkspaceRole: PropTypes.number,
  fetchNotesWithFilters: PropTypes.func,
  selectedFilters: PropTypes.object,
  hasActiveFilters: PropTypes.bool,
  viewMode: PropTypes.string,
  refreshStackedView: PropTypes.func,
};

NewNoteModal.defaultProps = {
  projects: [],
  jobs: [],
  prefilledData: null,
  defWorkSpaceId: null,
  userworksaces: [],
  source: "dashboard",
  defaultWorkspaceRole: null,
  fetchNotesWithFilters: null,
  selectedFilters: {},
  hasActiveFilters: false,
  viewMode: "cards",
  refreshStackedView: null,
};

export default NewNoteModal;